import React from 'react';
import { Link } from 'react-router-dom';
import { Info } from 'lucide-react';
import CircleProgress from "../../components/CircleProgress";

/**
 * Shared building blocks for the Role Match and Hire Confidence panels on the
 * candidate tile, so the two read as one system rather than two panels that
 * drifted apart.
 */

const RING_AMBER = '#F4A524';
const RING_TRAIL = '#EFEFF3';

/**
 * Marks a figure as AI-derived. Deliberately small and set high against the
 * text it follows — it annotates the verdict rather than competing with it.
 */
const AiSparkle = ({ className }) => (
    <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        className={`inline-block h-2 w-2 align-top ml-[3px] text-primary ${className || ''}`}
    >
        <path d="M12 0c.9 6.6 4.4 10.1 11 11-6.6.9-10.1 4.4-11 11-.9-6.6-4.4-10.1-11-11C7.6 10.1 11.1 6.6 12 0Z" />
    </svg>
);

/**
 * The score ring. Sized and weighted identically across both tabs so switching
 * between them doesn't shift the eye.
 */
/**
 * `isActive` drives the arc, not the number. Antd keeps an inactive tab pane
 * mounted, so the ring would otherwise fill once on first render and sit
 * static on every later visit; holding it at zero while hidden makes it replay
 * each time the tab is opened.
 */
const FitRing = ({ score, color = RING_AMBER, isActive = true }) => (
    <CircleProgress
        // `self-center` and `!p-0` undo `.metric-circle` in _talent.less, which
        // stretches the ring to the column width and insets it — the ring ends
        // up small and pinned left without them.
        className="metric-circle self-center w-[72px] !p-0"
        progress={isActive ? score : 0}
        strokeWidth={10}
        pathColor={color}
        trailColor={RING_TRAIL}
        // Butt caps, not round: a rounded cap overhangs the end of the arc by
        // half the stroke width at both ends, which swallows the remaining gap
        // entirely once a score passes ~95 and makes a 97 look like a 100.
        strokeLinecap="butt"
    >
        <span className="text-lg font-extrabold tabular-nums tracking-[-0.02em] text-[#121217]">
            {Math.round(score)}
        </span>
    </CircleProgress>
);

const VERDICT_TYPE = 'm-0 text-[15px] font-bold leading-[1.1] tracking-[-0.012em] text-[#0E0E14]';

/**
 * The headline call on the tile. Given a `to`, the whole verdict becomes the
 * way into the full breakdown on the profile — the text itself is the target,
 * with the icon as the cue rather than the hit area, so there is one obvious
 * thing to click instead of a small glyph beside a dead label.
 *
 * Navigates in place, like every other route on the tile.
 */
const FitVerdict = ({ children, to, title }) => {
    if (!to) {
        return (
            <p className={VERDICT_TYPE}>
                {children}
                <AiSparkle />
            </p>
        );
    }

    return (
        <Link
            to={to}
            title={title}
            // The tile's own click handlers sit above this one.
            onClick={(e) => e.stopPropagation()}
            // A *named* group: the tile itself is `group`, so a bare
            // `group-hover:` here would light the glyph up whenever the cursor
            // was anywhere on the card, which says nothing about what's clickable.
            className={`group/verdict inline-flex items-center gap-1 no-underline hover:text-primary ${VERDICT_TYPE}`}
        >
            <span>
                {children}
                <AiSparkle />
            </span>

            <Info
                className="h-3 w-3 shrink-0 text-[#C4C4CC] transition-colors duration-150 group-hover/verdict:text-primary"
                strokeWidth={2.25}
            />
        </Link>
    );
};

/**
 * A score value beside its label. Reads as a chip rather than plain bold text
 * so the number stays scannable down a column of tiles.
 */
/**
 * Carries no outer margin of its own — spacing belongs to whatever lays the
 * chip out, so it can sit in a flex row or a grid cell without fighting it.
 */
const ScoreChip = ({ children }) => (
    <b className="inline-block rounded-[5px] bg-[#F1F1F4] px-2 py-px text-center align-middle text-[11px] font-bold tabular-nums text-[#0E0E14] shadow-[inset_0_0_0_1px_#E4E4E9]">
        {children}
    </b>
);

/**
 * The row layout both fit tabs use for their sub-scores: label, a thin track,
 * the figure, and optionally a glyph saying there is more behind it.
 *
 * The bar earns its place where a bare number didn't: two scores are only
 * comparable at a glance if their lengths can be compared. Neutral grey,
 * because the tile already spends its colour on the ring and the links.
 *
 * `onClick` makes the entire row the target rather than a link inside the
 * label — at this size a fourteen-character link is a hard thing to hit and an
 * easy one to miss. `hasInfo` adds the glyph and the pointer without a
 * destination, for rows that are going to get one.
 */
const StatRow = ({ label, value, isActive = true, onClick, hasInfo, title }) => {
    const hasValue = value !== null && value !== undefined;

    // Held at zero while the tab is hidden so the track fills on every visit,
    // not only the first — antd keeps the inactive pane mounted.
    const fill = hasValue && isActive
        ? Math.min(Math.max(value, 0), 100)
        : 0;

    // A button only when there is something to click; otherwise a plain div, so
    // rows that merely explain themselves don't land in the tab order.
    const Element = onClick ? 'button' : 'div';

    return (
        <Element
            type={onClick ? 'button' : undefined}
            onClick={onClick}
            title={title}
            className={TB.mapClass(
                // Each row owns its own grid on an identical template, so labels,
                // bars and figures still line up in columns while the row stays a
                // single hover and click target — cells of one shared parent grid
                // could only be hovered and clicked piecemeal.
                // Named, because the tile is itself a `group`: an unnamed
                // `group-hover:` would fire from anywhere on the card and stop
                // saying which row the glyph belongs to.
                'group/stat grid w-full grid-cols-[104px_1fr_max-content] items-center gap-2 text-left',
                // The glyph earns the pointer even without a handler: those rows
                // are about to become links, and the cue is what's being tested.
                { 'cursor-pointer': !!onClick || !!hasInfo }
            )}
        >
            <span className="whitespace-nowrap text-right text-[11.5px] font-medium leading-tight text-[#595D68]">
                {label}
            </span>

            <span className="h-[3px] w-full overflow-hidden rounded-full bg-[#EFEFF3]">
                <span
                    className="block h-full rounded-full bg-[#4A4D57] transition-[width] duration-700 ease-out"
                    style={{ width: `${fill}%` }}
                />
            </span>

            {/* Figure and glyph share a cell rather than taking a column each:
                an empty fourth track would still draw its gap on the rows that
                have no icon, shortening their bars against the rows that do. */}
            <span className="flex items-center justify-end gap-1">
                <span className="min-w-[20px] text-right text-[11px] font-bold tabular-nums leading-none text-[#0E0E14]">
                    {hasValue ? Math.round(value) : <span className="text-[#C4C4CC]">—</span>}
                </span>

                {/* Grey until the row is hovered anywhere along its length —
                    that is what says the whole row is the target, not the glyph
                    alone.

                    Nudged up a pixel: centring the boxes is not the same as
                    centring what you see. The figure and the label are digits
                    and caps sitting on a baseline with their descender space
                    empty, so their ink rides high in the line box, while a
                    circle fills its box evenly and reads a pixel low beside
                    them. `relative` keeps the correction out of the layout. */}
                {hasInfo && (
                    <Info
                        className="relative -top-px h-3 w-3 shrink-0 text-[#C4C4CC] transition-colors duration-150 group-hover/stat:text-primary"
                        strokeWidth={2.25}
                    />
                )}
            </span>
        </Element>
    );
};

/**
 * Owns the breathing room around the score rows, so the gap either side of
 * them is identical on both tabs and neighbouring blocks don't add their own.
 *
 * A flex column, not a grid: each row now carries its own matching grid so it
 * can be hovered and clicked as one piece.
 */
const StatGrid = ({ children }) => (
    <div className="mx-auto mt-2 mb-2.5 flex w-full max-w-[252px] flex-col gap-2">
        {children}
    </div>
);

/**
 * Marks a score as under evaluation rather than something to act on.
 *
 * The explicit `leading-none` and padding matter more than they look: at
 * `py-px` the label had almost no box of its own, so the tile's hover lift
 * moved it across a subpixel boundary and the text visibly jittered.
 */
const BetaPill = () => (
    <span className="inline-flex items-center rounded-[4px] bg-primary px-1.5 py-[3px] text-[8px] font-bold uppercase leading-none tracking-[0.09em] text-white">
        Beta
    </span>
);

const SubScore = ({ label, children }) => (
    <span className="inline-flex items-center gap-2 text-[11.5px] font-medium leading-[1.7] text-[#595D68]">
        {label}
        <ScoreChip>{children}</ScoreChip>
    </span>
);

/**
 * Emphasises the figures inside a backend-authored note.
 *
 * Lines like "5 rehires across 1 client" arrive as one string, but the numbers
 * are what a reader scans for — so they get the same weight the hand-built
 * counts elsewhere have, while the prose around them stays quiet.
 *
 * The split pattern is global (it has to be, to find every match) but the test
 * pattern is not: reusing one global regex for both would carry `lastIndex`
 * between calls and skip matches.
 */
const NUMERIC_SEGMENT = /^\d[\d,.]*%?$/;

const withBoldNumbers = (text) => {
    if (!text) {
        return null;
    }

    return TB.map(String(text).split(/(\d[\d,.]*%?)/g), (part, index) => (
        NUMERIC_SEGMENT.test(part)
            ? <b key={index} className="font-semibold text-[#121217]">{part}</b>
            : part
    ));
};

export {
    withBoldNumbers,
    StatRow,
    StatGrid,
    BetaPill,
    AiSparkle,
    FitRing,
    FitVerdict,
    ScoreChip,
    SubScore,
    RING_AMBER,
    RING_TRAIL,
};
